import { useState } from 'react';
import { useTheme } from '@flowcore-water/ui';
import { AudioPlayer } from './AudioPlayer';
import { ImageLightbox } from './ImageLightbox';

/** Fields known to contain asset URLs, keyed by entity. */
const AUDIO_FIELDS = new Set(['recording_url']);
const IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg'];

function isImageUrl(url: string): boolean {
  const lower = url.toLowerCase();
  return IMAGE_EXTENSIONS.some((ext) => lower.includes(ext));
}

function isAudioField(field: string): boolean {
  return AUDIO_FIELDS.has(field);
}

interface AssetRendererProps {
  entity: string;
  recordId: number;
  field: string;
  value: string;
}

/**
 * Renders asset fields as rich widgets (audio player, image thumbnail).
 * Falls back to a plain link for unrecognized asset types.
 */
export function AssetRenderer({ entity, recordId, field, value }: AssetRendererProps) {
  const { t } = useTheme();
  const [lightboxOpen, setLightboxOpen] = useState(false);

  // Audio recordings — proxy through backend to avoid CORS
  if (isAudioField(field)) {
    const proxiedUrl = `/browse/assets/${entity}/${recordId}/${field}`;
    return <AudioPlayer src={proxiedUrl} />;
  }

  // Image URLs — show thumbnail with lightbox
  if (isImageUrl(value)) {
    return (
      <>
        <button
          onClick={() => setLightboxOpen(true)}
          className="group relative overflow-hidden rounded-lg border"
          style={{ borderColor: t.borderSubtle }}
        >
          <img
            src={value}
            alt={field}
            className="h-24 w-auto object-cover transition-opacity group-hover:opacity-80"
          />
          <span
            className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 transition-opacity group-hover:opacity-100 text-xs text-white font-medium"
          >
            View
          </span>
        </button>
        {lightboxOpen && (
          <ImageLightbox src={value} alt={field} onClose={() => setLightboxOpen(false)} />
        )}
      </>
    );
  }

  // Fallback: clickable link for other URLs
  return (
    <a
      href={value}
      target="_blank"
      rel="noopener noreferrer"
      className="text-sm underline"
      style={{ color: t.accent }}
    >
      Open file
    </a>
  );
}

/** Check if a field+value pair should be rendered as an asset widget. */
export function isAssetField(field: string, value: unknown): boolean {
  if (typeof value !== 'string' || !value) return false;
  if (isAudioField(field)) return true;
  if (isImageUrl(value)) return true;
  // URL fields ending in _url that point to files
  if (field.endsWith('_url') && (value.startsWith('http://') || value.startsWith('https://'))) {
    return true;
  }
  return false;
}
